import React, { useState, useEffect, useContext, useCallback, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Lock, Calculator, AlertTriangle, Pencil } from 'lucide-react';

const money = (v) => (v === null || v === undefined || v === '' ? null
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => `${(Number(v || 0) * 100).toFixed(0)}%`;

const Row = ({ label, value, testId, strong, negative, sub }) => (
  <>
    <span className={`text-slate-500 ${sub ? 'pl-3 text-[13px]' : ''} ${strong ? 'font-bold text-[#0E2A47] border-t pt-2' : ''}`}>{label}</span>
    <span data-testid={testId}
      className={`text-right ${strong ? 'font-extrabold border-t pt-2' : 'font-medium'} ${negative ? 'text-[#C8641E]' : 'text-[#0E2A47]'}`}>
      {value ?? '—'}
    </span>
  </>
);

const nnum = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const roundUpCents = (v) => Math.ceil((Number(v) - 1e-9) * 100) / 100;

// Live client-side port of the backend compute_costing (preview only; the server
// remains the source of truth on Save).
const computeLocal = (form, rates, liveClientCharge) => {
  const mode = form.labor_mode || 'none';
  const labor_amount = mode === 'hourly'
    ? Math.round(nnum(form.labor_hours) * nnum(form.labor_rate) * 100) / 100
    : mode === 'flat' ? nnum(form.labor_flat_amount) : 0;
  const burden_rate = form.labor_classification === 'employee' ? nnum(rates.emp_burden_rate) : 0;
  const burden_amount = Math.round(labor_amount * burden_rate * 100) / 100;
  const travel_amount = Math.round(nnum(form.travel_hours) * nnum(rates.travel_rate) * 100) / 100;
  const tolls = nnum(form.tolls_parking);
  const supplies = nnum(form.supplies);
  const materials_cost = nnum(form.materials_cost);
  const other_direct = nnum(form.other_direct);
  const total_direct_cost = Math.round((labor_amount + burden_amount + travel_amount + tolls + supplies + materials_cost + other_direct) * 100) / 100;
  const cc = Math.round(nnum(liveClientCharge) * 100) / 100;
  const overhead_rate = nnum(rates.overhead_rate);
  const min_profit = nnum(rates.min_profit);
  const overhead_amount = Math.round(cc * overhead_rate * 100) / 100;
  const gross_profit = Math.round((cc - total_direct_cost) * 100) / 100;
  const profit_after_overhead = Math.round((gross_profit - overhead_amount) * 100) / 100;
  const below_minimum = profit_after_overhead < min_profit;
  const denom = (1 - overhead_rate) || 1;
  let rec = roundUpCents((total_direct_cost + min_profit) / denom);
  let guard = 0;
  while (Math.round((rec * (1 - overhead_rate) - total_direct_cost) * 100) / 100 < min_profit && guard < 200) {
    rec = Math.round((rec + 0.01) * 100) / 100; guard += 1;
  }
  return {
    labor_amount, burden_rate, burden_amount, travel_amount, travel_rate: nnum(rates.travel_rate),
    travel_hours: nnum(form.travel_hours), tolls_parking: tolls, supplies, materials_cost, other_direct,
    total_direct_cost, total_client_charge: cc, overhead_rate, overhead_amount, gross_profit,
    profit_after_overhead, min_profit, below_minimum, min_recommended_client_charge: rec,
    labor_mode: mode, labor_classification: form.labor_classification || 'employee',
  };
};

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// Internal job costing — labor, travel, supplies, overhead and profit.
// Never shown on customer-facing reports.
export const CostingPanel = forwardRef(({ workOrderId, canEdit, refreshKey, editing = false, liveClientCharge }, ref) => {
  const { token, user } = useContext(AuthContext);
  const isOwner = user?.role === 'owner';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null); // 'rates'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [rates, setRates] = useState({ overhead_rate: '', travel_hourly_rate: '' });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/work-orders/${workOrderId}/costing`, authHeaders);
      setData(r.data);
    } catch (e) { /* internal read only */ }
  }, [workOrderId, token, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const seedForm = useCallback((ic) => setForm({
    labor_mode: ic.labor_mode || 'none',
    labor_hours: ic.labor_hours ?? '',
    labor_rate: ic.labor_rate ?? '',
    labor_flat_amount: ic.labor_flat_amount ?? '',
    labor_classification: ic.labor_classification || 'employee',
    actual_time_worked_hours: ic.actual_time_worked_hours ?? '',
    travel_hours: ic.travel_hours ?? '',
    tolls_parking: ic.tolls_parking ?? '',
    supplies: ic.supplies ?? '',
    materials_cost: ic.materials_cost ?? '',
    other_direct: ic.other_direct ?? '',
    confirmed_zero: !!ic.confirmed_zero,
  }), []);

  // Seed the inline form when entering edit mode.
  useEffect(() => { if (editing && data) seedForm(data.internal_costing || {}); }, [editing, data, seedForm]);

  const doSave = useCallback(async () => {
    await axios.post(`${API}/work-orders/${workOrderId}/costing`, {
      labor_mode: form.labor_mode,
      labor_hours: num(form.labor_hours),
      labor_rate: num(form.labor_rate),
      labor_flat_amount: num(form.labor_flat_amount),
      labor_classification: form.labor_classification,
      actual_time_worked_hours: num(form.actual_time_worked_hours),
      travel_hours: num(form.travel_hours),
      tolls_parking: num(form.tolls_parking),
      supplies: num(form.supplies),
      materials_cost: num(form.materials_cost),
      other_direct: num(form.other_direct),
      confirmed_zero: !!form.confirmed_zero,
    }, authHeaders);
  }, [form, workOrderId, token]);

  // Parent (page-level Save) calls this to persist inline costing changes.
  useImperativeHandle(ref, () => ({ save: doSave }), [doSave]);

  if (!data) return null;
  const b = data.breakdown;
  const ic = data.internal_costing || {};

  const openRates = () => { setRates({ overhead_rate: b.overhead_rate, travel_hourly_rate: b.travel_rate }); setDlg('rates'); };
  const saveRates = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/rout-settings`, {
        overhead_rate: Number(rates.overhead_rate),
        travel_hourly_rate: Number(rates.travel_hourly_rate),
      }, authHeaders);
      toast.success('Company rates updated'); setDlg(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update rates'); }
    finally { setBusy(false); }
  };

  const inlineEdit = editing && canEdit;
  const empBurden = ic.rates_snapshot?.labor_burden_rate_employee
    ?? (b.labor_classification === 'employee' ? b.burden_rate : 0);
  const liveCC = (liveClientCharge === undefined || liveClientCharge === null || liveClientCharge === '')
    ? b.total_client_charge : Number(liveClientCharge);
  const view = inlineEdit
    ? computeLocal(form, { overhead_rate: b.overhead_rate, travel_rate: b.travel_rate, emp_burden_rate: empBurden, min_profit: b.min_profit }, liveCC)
    : b;

  return (
    <div className="rounded-xl border border-[#0E2A47]/15 bg-[#0E2A47]/[0.03] p-4" data-testid="wo-internal-costing">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
            <Lock size={11} /> Internal — not on client reports
          </span>
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Internal Job Costing</span>
        </div>
        {inlineEdit && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5a8a1e]"><Calculator size={12} /> Editing — recalculates live</span>}
      </div>

      {inlineEdit && form.labor_mode !== undefined && (
        <div className="mb-4 space-y-3 rounded-lg border border-[#8FC93A]/40 bg-white p-3" data-testid="wo-costing-inline-editor">
          <div>
            <Label className="text-xs">Labor</Label>
            <div className="mt-1 flex gap-2">
              {['none', 'hourly', 'flat'].map((m) => (
                <button key={m} type="button" onClick={() => setForm({ ...form, labor_mode: m })} data-testid={`labor-mode-${m}`}
                  className={`rounded-lg border px-3 py-1.5 text-sm capitalize ${form.labor_mode === m ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-200 text-slate-600'}`}>
                  {m === 'none' ? 'No payout' : m}
                </button>
              ))}
            </div>
            {form.labor_mode === 'hourly' && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Hours</Label><Input type="number" step="0.25" min="0" value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} data-testid="labor-hours-input" /></div>
                <div><Label className="text-xs">Rate ($/hr)</Label><Input type="number" step="0.01" min="0" value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })} data-testid="labor-rate-input" /></div>
              </div>
            )}
            {form.labor_mode === 'flat' && (
              <div className="mt-2"><Label className="text-xs">Flat Amount ($)</Label><Input type="number" step="0.01" min="0" value={form.labor_flat_amount} onChange={(e) => setForm({ ...form, labor_flat_amount: e.target.value })} data-testid="labor-flat-input" /></div>
            )}
            <div className="mt-2 flex gap-2">
              {['employee', 'service_provider'].map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, labor_classification: c })} data-testid={`labor-class-${c}`}
                  className={`rounded-lg border px-3 py-1 text-xs ${form.labor_classification === c ? 'border-[#0E2A47] bg-slate-100 font-semibold text-[#0E2A47]' : 'border-slate-200 text-slate-500'}`}>
                  {c === 'employee' ? 'Employee (burden applies)' : 'Service Provider (no burden)'}
                </button>
              ))}
            </div>
            <div className="mt-2"><Label className="text-xs">Actual time worked (hr) — recorded separately from payout</Label><Input type="number" step="0.25" min="0" value={form.actual_time_worked_hours} onChange={(e) => setForm({ ...form, actual_time_worked_hours: e.target.value })} data-testid="actual-time-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 border-t pt-3">
            <div><Label className="text-xs">Travel Hours (× company travel rate)</Label><Input type="number" step="0.25" min="0" value={form.travel_hours} onChange={(e) => setForm({ ...form, travel_hours: e.target.value })} data-testid="travel-hours-input" /></div>
            <div><Label className="text-xs">Tolls & Parking ($)</Label><Input type="number" step="0.01" min="0" value={form.tolls_parking} onChange={(e) => setForm({ ...form, tolls_parking: e.target.value })} data-testid="tolls-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2 border-t pt-3">
            <div><Label className="text-xs">Supplies ($)</Label><Input type="number" step="0.01" min="0" value={form.supplies} onChange={(e) => setForm({ ...form, supplies: e.target.value })} data-testid="supplies-input" /></div>
            <div><Label className="text-xs">Materials ($)</Label><Input type="number" step="0.01" min="0" value={form.materials_cost} onChange={(e) => setForm({ ...form, materials_cost: e.target.value })} data-testid="materials-cost-input" /></div>
            <div><Label className="text-xs">Other Direct ($)</Label><Input type="number" step="0.01" min="0" value={form.other_direct} onChange={(e) => setForm({ ...form, other_direct: e.target.value })} data-testid="other-direct-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={!!form.confirmed_zero} onChange={(e) => setForm({ ...form, confirmed_zero: e.target.checked })} data-testid="confirmed-zero-check" />
            Costs are confirmed complete (a confirmed $0 is not a missing value)
          </label>
        </div>
      )}

      {!inlineEdit && !b.has_costing_inputs && (
        <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500" data-testid="wo-costing-empty">
          No internal costs entered yet. Press "Edit" on the work order to record labor, travel, supplies and materials. A confirmed $0 cost is different from a missing cost.
        </div>
      )}

      <div className="grid grid-cols-2 gap-y-1.5 gap-x-6 text-sm max-w-lg">
        <Row label={`Labor (${view.labor_mode === 'hourly' ? 'hourly' : view.labor_mode === 'flat' ? 'flat' : 'none'}${view.labor_classification === 'service_provider' ? ' · provider' : ' · employee'})`} value={money(view.labor_amount)} testId="cost-labor" />
        {view.burden_amount > 0 && <Row sub label={`Labor Burden (${pct(view.burden_rate)}, employee)`} value={money(view.burden_amount)} testId="cost-burden" />}
        <Row label={`Travel (${view.travel_hours || 0} hr × ${money(view.travel_rate)})`} value={money(view.travel_amount)} testId="cost-travel" />
        {view.tolls_parking > 0 && <Row sub label="Tolls & Parking" value={money(view.tolls_parking)} testId="cost-tolls" />}
        <Row label="Supplies" value={money(view.supplies)} testId="cost-supplies" />
        <Row label="Materials" value={money(view.materials_cost)} testId="cost-materials-cost" />
        <Row label="Other Direct" value={money(view.other_direct)} testId="cost-other" />
        <Row strong label="Total Direct Cost" value={money(view.total_direct_cost)} testId="cost-total-direct" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-y-1.5 gap-x-6 text-sm max-w-lg">
        <Row label="Total Client Charge" value={money(view.total_client_charge)} testId="cost-client-charge" />
        <Row label={`Overhead (${pct(view.overhead_rate)} of charge)`} value={money(view.overhead_amount)} testId="cost-overhead" />
        <Row label="Gross Profit" value={money(view.gross_profit)} negative={view.gross_profit < 0} testId="cost-gross-profit" />
        <Row strong label="Profit After Overhead" value={money(view.profit_after_overhead)} negative={view.profit_after_overhead < view.min_profit} testId="cost-profit-after-overhead" />
      </div>

      {(!inlineEdit && !b.has_costing_inputs && !b.confirmed_zero) ? (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="wo-costing-pending">
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={14} /> Costs Pending</div>
          <div className="mt-0.5 text-xs">Direct costs have not been entered or confirmed as $0. Profit and compliance are withheld until costs are recorded or explicitly confirmed as $0 (press "Edit").</div>
        </div>
      ) : view.below_minimum ? (
        <div className="mt-3 rounded-lg border border-[#C8641E]/40 bg-[#C8641E]/10 px-3 py-2 text-sm text-[#8a3f10]" data-testid="wo-costing-below-min">
          <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={14} /> Below minimum profit</div>
          <div className="mt-0.5 text-xs">
            Every new billable tag job must clear at least {money(view.min_profit)} Profit After Overhead. Minimum recommended Total Client Charge:
            <b className="ml-1" data-testid="cost-min-recommended">{money(view.min_recommended_client_charge)}</b>. This pricing cannot be approved as compliant. (Historical jobs keep their agreed pricing.)
          </div>
        </div>
      ) : view.total_client_charge > 0 ? (
        <div className="mt-3 text-xs text-emerald-700" data-testid="wo-costing-ok">
          Meets the {money(view.min_profit)} minimum profit requirement.
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
        <span>Rates: Overhead {pct(b.overhead_rate)} · Travel {money(b.travel_rate)}/hr · Min profit {money(b.min_profit)}</span>
        {isOwner
          ? <button onClick={openRates} className="inline-flex items-center gap-1 font-medium text-[#0E2A47] hover:underline" data-testid="wo-edit-rates-btn"><Pencil size={11} /> Edit rates</button>
          : <span className="inline-flex items-center gap-1"><Lock size={10} /> Rates locked (Global Administrator)</span>}
      </div>

      {/* Owner-only rate edit */}
      <Dialog open={dlg === 'rates'} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="wo-rates-dialog">
          <DialogHeader><DialogTitle>Company Rate Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-slate-400">Global Administrator only. Changing these does not re-price finalized jobs (their rates are preserved) and does not affect TALLY.</p>
            <div><Label>ROUT Overhead Rate (e.g. 0.17 = 17%)</Label>
              <Input type="number" step="0.01" min="0" max="0.99" value={rates.overhead_rate} onChange={(e) => setRates({ ...rates, overhead_rate: e.target.value })} data-testid="rate-overhead-input" /></div>
            <div><Label>Standard Travel Rate ($/hr)</Label>
              <Input type="number" step="0.01" min="0" value={rates.travel_hourly_rate} onChange={(e) => setRates({ ...rates, travel_hourly_rate: e.target.value })} data-testid="rate-travel-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={saveRates} disabled={busy} data-testid="rates-save-btn">Save Rates</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default CostingPanel;
